import * as Popover from '@radix-ui/react-popover';
import { useEffect, useState } from 'react';

import { Button } from '@/components/Button/Button';

import type { ActiveAnnotationSelection } from './annotationTypes';
import { NoteEditor } from './NoteEditor';
import styles from './SelectionPopover.module.css';

interface SelectionPopoverProps {
  onCreateHighlight: (selection: ActiveAnnotationSelection) => Promise<void> | void;
  onCreateNote: (
    selection: ActiveAnnotationSelection,
    note: string,
  ) => Promise<void> | void;
  onOpenChange: (nextOpen: boolean) => void;
  open: boolean;
  selection: ActiveAnnotationSelection | null;
}

export function SelectionPopover({
  onCreateHighlight,
  onCreateNote,
  onOpenChange,
  open,
  selection,
}: SelectionPopoverProps) {
  const [mode, setMode] = useState<'actions' | 'note'>('actions');

  useEffect(() => {
    if (!open) {
      setMode('actions');
    }
  }, [open, selection?.draft.id]);

  if (!selection) {
    return null;
  }

  return (
    <Popover.Root onOpenChange={onOpenChange} open={open}>
      <Popover.Anchor asChild>
        <div
          aria-hidden="true"
          className={styles.anchor}
          style={{
            height: selection.rect.height,
            left: selection.rect.left,
            top: selection.rect.top,
            width: selection.rect.width,
          }}
        />
      </Popover.Anchor>
      <Popover.Portal>
        <Popover.Content
          align="start"
          className={styles.content}
        onOpenAutoFocus={(event) => event.preventDefault()}
        side="top"
        sideOffset={10}
      >
          {mode === 'note' ? (
            <NoteEditor
              cancelLabel="Back"
              onCancel={() => setMode('actions')}
              onSave={async (note) => {
                await onCreateNote(selection, note);
              }}
              saveLabel="Save note"
            />
          ) : (
            <div className={styles.actions}>
              <p className={styles.prompt}>
                Save this selection as a highlight or add a private note.
              </p>
              <blockquote className={styles.quote}>{selection.selectedText}</blockquote>
              <div className={styles.buttonRow}>
                <Button
                  onClick={() => {
                    void onCreateHighlight(selection);
                  }}
                  size="small"
                  variant="secondary"
                >
                  Highlight
                </Button>
                <Button
                  onClick={() => {
                    setMode('note');
                  }}
                  size="small"
                  variant="primary"
                >
                  Add note
                </Button>
              </div>
            </div>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
